/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { identity, map, Observable, startWith, takeLast } from 'rxjs'
import { ErrorHandlerService } from './error-handler.service'
import { environment } from '../../environments/environment'
import { ChunkedDownloadDTO } from '../models/chunked-download.dto'

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private baseUrl: string

  constructor(private http: HttpClient, private errorHandlerService: ErrorHandlerService) {
    this.baseUrl = environment.baseUrlApi
  }

  private download(response: any): void {
    let contentDisposition = response.headers.get('Content-Disposition')
    let filename = contentDisposition.split(';')[1].split('=')[1].trim().replace(/\"/g, '')
    let binaryData = []
    binaryData.push(response.body)
    let downloadLink = document.createElement('a')
    downloadLink.setAttribute('download', filename)
    downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: response.type }))
    document.body.appendChild(downloadLink)
    downloadLink.click()
    downloadLink.remove()
  }

  private request(url: string): Observable<any> {
    return this.http
      .get<any>(url, { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  arquivoDownload(path: string, name: string): void {
    let url = `${this.baseUrl}/arquivo/${path}/${name}`
    this.request(url).subscribe((response: any) => this.download(response))
  }

  arquivoDownloadUrl(url: string): void {
    this.request(url).subscribe((response: any) => this.download(response))
  }

  private arquivoObjectURL(url: string): Observable<string> {    
    return this.request(url).pipe(
      map(response => {
        const mimeType = response.headers.get('Content-Type') || 'application/octet-stream';
        const binaryData = [];
        binaryData.push(response.body);
        return window.URL.createObjectURL(new Blob(binaryData, { type: mimeType }));
      }),
      startWith(''),
      takeLast(1)
    )
  }

  edital(id: number) {
    let url = `${this.baseUrl}/status-documentacao/edital/${id}`
    return this.request(url).subscribe((response: any) => this.download(response))
  }

  arquivoObjectURLPorPath(path: string, name: string): Observable<string> {
    return this.arquivoObjectURL(`${this.baseUrl}/arquivo/${path}/${name}`)
  }

  async getObjectURLViaChunks(chunkedDownload: ChunkedDownloadDTO, progressCallback?: (progress: number) => void): Promise<string> {
    const totalChunks = chunkedDownload.chunks.length
    let completedChunks = 0
    
    const chunkPromises = chunkedDownload.chunks.map(async (chunk, index) => {
      const response = await fetch(chunk.url, {
        headers: {
          'Range': `bytes=${chunk.startByte}-${chunk.endByte}`
        }
      })
      const result = { 
        index, 
        arrayBuffer: await response.arrayBuffer(),
        contentType: response.headers.get('Content-Type')
      }
      
      completedChunks++
      if (progressCallback) {
        const progress = Math.round((completedChunks / totalChunks) * 100)
        progressCallback(progress)
      }
      
      return result
    })
    
    const results = await Promise.all(chunkPromises)
    const orderedChunks = results.sort((a, b) => a.index - b.index).map(r => r.arrayBuffer)
    const mimeType = results[0]?.contentType || this.getMimeTypeFromFileName(chunkedDownload.fileName)
    
    const blob = new Blob(orderedChunks, { type: mimeType })
    return window.URL.createObjectURL(blob)
  }

  private getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'txt': 'text/plain',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }
    return mimeTypes[extension || ''] || 'application/octet-stream'
  }
}
